/*******************************************************************************
* Licensed Materials - Property of HCL
* (c) Copyright HCL Technologies Ltd. 2020, 2023. All Rights Reserved.

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Checkbox, Input, Panel, Text } from "vr-react-components";
import { handleSetKeyboard } from "../../handlers/PrivateHandlers";
import { setClue } from "../../store/slice/centerGraphSlice";

const BarWidgetCluePanel = React.memo(({
    stateName,
    width,
    height,
    axe,
    ...otherAttributes
}) => {
    const clue = useSelector((state) => state[stateName].position.clue);
    const canModify = useSelector((state) => state.private.device.canModify);

    const [isInputFocused, setIsInputFocused] = useState(false);
    const [currentClue, setCurrentClue] = useState(clue);
    const [isClueSaved, setIsClueSaved] = useState(clue.isClueSaved ? clue.isClueSaved : '');

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(setClue(currentClue))
    }, [dispatch, currentClue]);

    useEffect(() => {
        return () => {
            handleSetKeyboard("num", false, null)
        };
    }, []);

    const handleInput = () => {
        setIsInputFocused(true);

        handleSetKeyboard("num", true, (callback) => {
            setCurrentClue((old) => {
                return {
                    ...old,
                    axe,
                    isClueSaved,
                    threshold: callback(old.threshold ? old.threshold : ''),
                };
            })
        });
    }

    return (
        <Panel 
            id="right45"
            width={width}
            height={height}
            panel
            aframeClasses={canModify ? "collidable" : ""}
            {...otherAttributes}
        >
            <a-entity
                position={`${-width / 2 + width * 0.05} ${height / 2 - height / 10
                    } 0.01`}
            >
                <Text
                    text={`Set a visual threshold for ${axe}`}
                    width={width * 0.9}
                    align="left"
                    size="p3"
                    baseline="top"
                />
                <Text
                    value="Threshold"
                    width={width}
                    align="left"
                    size="p4"
                    position={`0 ${-height / 5 - (height / 10) * 2} 0`}
                />
                <Checkbox
                    side={height / 10}
                    aframeClasses={canModify ? "collidable" : ""}
                    text={"Keep when leaving Exploration Mode"}
                    textWidth={(width / 8) * 5}
                    textSize="p5"
                    checked={isClueSaved && clue.threshold}
                    disabled={!clue.threshold}
                    setChecked={() => {
                        setIsInputFocused(false);
                        setIsClueSaved(old => !old);
                        setCurrentClue((old) => {
                            return {
                                ...old,
                                isClueSaved: !isClueSaved,
                            }
                        })
                        handleSetKeyboard("num", false, null)
                    }}
                    position={`${((width * 0.8) / 8) * 3} ${-height / 5 - (height / 10) * 4
                        } 0`}
                />
                <Input
                    width={(width * 0.8) / 4}
                    height={height / 5}
                    aframeClasses={canModify ? "collidable" : ""}
                    position={`${(width * 0.8) / 8} ${-height / 5 - (height / 10) * 4
                        } 0`}
                    text={currentClue.threshold}
                    textSize="p5"
                    focused={isInputFocused}
                    onClickHandler={handleInput}
                />
            </a-entity>
        </Panel>
    );
},
);

export default BarWidgetCluePanel;
